import React, { useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { app as appUI } from "@markedesk/plugin-sdk/ui";
import PluginHtmlInline from "./PluginHtmlInline";
import { useChannelPlugins } from "../ChannelIcon";

/**
 * Camada que cobre o app inteiro enquanto um plugin declarar uma action no slot
 * `app-overlay`.
 *
 * É o ponto para um fluxo que precisa ser concluído ANTES de o sistema poder
 * ser usado — onboarding obrigatório, aceite pendente, bloqueio por
 * inadimplência. Diferente do `PluginHtmlModalHost`, que é modal dispensável e
 * aberto por vontade do usuário: aqui não há botão de fechar, porque quem
 * decide que acabou é o plugin, parando de declarar a action.
 *
 * O app não sabe o que está sendo mostrado nem por quê. Ele só sabe que existe
 * uma action neste slot e que, enquanto existir, o resto não é alcançável.
 *
 * ## Por que cobre tudo, e não só o conteúdo
 *
 * Bloquear apenas a área central deixaria a navbar e o menu lateral clicáveis —
 * o usuário sairia do fluxo pela porta lateral e o "obrigatório" viraria
 * decorativo. Por isso o host é montado FORA do `<div>` do conteúdo, com
 * `position: fixed` e z-index acima do drawer do Material-UI (1200) e do modal
 * (1300): o overlay é o topo da pilha.
 *
 * ## Ciclo de vida
 *
 * A action some do metadata quando o plugin considera o fluxo concluído. Como o
 * `/plugins` é cacheado no módulo do app (`pluginsCache`), o plugin precisa
 * chamar `markedesk.clearPluginsCache()` ao concluir para a tela liberar sem
 * F5 — é o mesmo caminho que instalar/remover plugin já usa.
 */
const useStyles = makeStyles((theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    // Acima do drawer (1200) e do modal (1300) do MUI: nada do app deve
    // aparecer por cima de um bloqueio.
    zIndex: 1400,
    background: theme.palette.background.default,
    overflow: "auto",
  },
}));

export default function PluginOverlayHost() {
  const classes = useStyles();
  const theme = useTheme();
  const plugins = useChannelPlugins();

  /**
   * Primeira action declarada no slot. Só uma vale: duas telas bloqueantes
   * disputando o mesmo espaço não têm ordem definida, e empilhar as duas
   * deixaria o usuário preso sem entender qual concluir. Quem declarou primeiro
   * bloqueia; a outra aparece quando a primeira liberar.
   */
  const acao = useMemo(() => {
    for (const p of plugins || []) {
      for (const a of p.provides?.actions || []) {
        if (a.slot === appUI.slots.OVERLAY) return { ...a, plugin: p };
      }
    }
    return null;
  }, [plugins]);

  /**
   * O plugin monta o HTML no servidor dele, longe do navegador: sem receber as
   * cores e o idioma, teria de adivinhar os dois. E a única pista que o CSS tem
   * sozinho (`prefers-color-scheme`) é a do SISTEMA OPERACIONAL, que não tem
   * relação com o tema escolhido no app — app escuro + Windows claro daria uma
   * tela branca cobrindo tudo. Contrato: `TemaDoApp` no SDK.
   */
  const contexto = useMemo(
    () => ({
      idioma: i18n.language,
      tema: {
        modo: theme.palette.type,
        primaria: theme.palette.primary.main,
        primariaTexto: theme.palette.primary.contrastText,
        superficie: theme.palette.background.paper,
        fundo: theme.palette.background.default,
        texto: theme.palette.text.primary,
        textoSecundario: theme.palette.text.secondary,
        borda: theme.palette.divider,
        sucesso: theme.palette.success?.main || "#4caf50",
      },
    }),
    [theme]
  );

  if (!acao) return null;

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true">
      <PluginHtmlInline
        block
        routePath={acao.plugin.routePath}
        endpoint={acao.endpoint || `/${acao.id}`}
        html={acao.html}
        context={contexto}
      />
    </div>
  );
}
